import { useState } from 'react';
import {
  CheckCircle2,
  ChevronRight,
  CircleDashed,
  CircleSlash,
  Loader2,
  XCircle,
} from 'lucide-react';
import type { DagNodeState, WorkflowStepStatus } from '@/lib/types';
import { formatDurationMs } from '@/lib/format';

interface WorkflowRunStepperProps {
  nodes: DagNodeState[];
}

function StepIcon({ status }: { status: WorkflowStepStatus }): React.ReactElement {
  switch (status) {
    case 'completed':
      return <CheckCircle2 className="h-5 w-5 text-success" />;
    case 'failed':
      return <XCircle className="h-5 w-5 text-error" />;
    case 'running':
      return <Loader2 className="h-5 w-5 animate-spin text-primary" />;
    case 'skipped':
      return <CircleSlash className="h-5 w-5 text-text-tertiary" />;
    case 'pending':
    default:
      return <CircleDashed className="h-5 w-5 text-text-tertiary" />;
  }
}

const SKIP_REASON_LABEL: Record<NonNullable<DagNodeState['reason']>, string> = {
  when_condition: 'Skipped — when condition not met',
  trigger_rule: 'Skipped — trigger rule',
  prior_success: 'Skipped — prior success',
};

/** Whether a node carries any detail worth expanding to show. */
export function hasDetail(node: DagNodeState): boolean {
  return (
    node.error !== undefined ||
    node.duration !== undefined ||
    (node.costUsd !== undefined && node.costUsd > 0) ||
    node.reason !== undefined ||
    node.currentIteration !== undefined
  );
}

/** Expanded per-node detail — surfaces the fields already on DagNodeState. */
export function NodeDetail({ node }: { node: DagNodeState }): React.ReactElement {
  return (
    <div className="mt-1 space-y-1 rounded-md bg-surface-elevated px-2 py-1.5 text-xs">
      {node.error !== undefined && (
        <p className="whitespace-pre-wrap break-words text-error" title={node.error}>
          {node.error}
        </p>
      )}
      {node.duration !== undefined && (
        <p className="text-text-secondary">
          <span className="text-text-tertiary">Duration:</span> {formatDurationMs(node.duration)}
        </p>
      )}
      {node.costUsd !== undefined && node.costUsd > 0 && (
        <p className="text-text-secondary">
          <span className="text-text-tertiary">Cost:</span> ${node.costUsd.toFixed(4)}
        </p>
      )}
      {node.currentIteration !== undefined && (
        <p className="text-text-secondary">
          <span className="text-text-tertiary">Iteration:</span> {node.currentIteration}
        </p>
      )}
      {node.reason !== undefined && (
        <p className="text-text-tertiary">{SKIP_REASON_LABEL[node.reason]}</p>
      )}
    </div>
  );
}

/** Mobile vertical-timeline rendering of a workflow run's DAG nodes. */
export function WorkflowRunStepper({ nodes }: WorkflowRunStepperProps): React.ReactElement {
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());

  if (nodes.length === 0) {
    return (
      <div className="flex items-center justify-center py-8 text-sm text-text-tertiary">
        No steps yet.
      </div>
    );
  }

  function toggle(nodeId: string): void {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(nodeId)) next.delete(nodeId);
      else next.add(nodeId);
      return next;
    });
  }

  return (
    <ol className="flex flex-1 flex-col gap-1 overflow-auto p-3">
      {nodes.map((node, idx) => {
        const expandable = hasDetail(node);
        const isOpen = expanded.has(node.nodeId);
        return (
          <li key={node.nodeId} className="flex items-start gap-3">
            <div className="flex flex-col items-center">
              <StepIcon status={node.status} />
              {idx < nodes.length - 1 && <div className="my-1 h-6 w-px bg-border" />}
            </div>
            <div className="min-w-0 flex-1 pb-2">
              {expandable ? (
                <button
                  type="button"
                  onClick={() => {
                    toggle(node.nodeId);
                  }}
                  aria-expanded={isOpen}
                  className="flex w-full items-center gap-1 text-left"
                >
                  <ChevronRight
                    className={`h-3.5 w-3.5 shrink-0 text-text-tertiary transition-transform ${
                      isOpen ? 'rotate-90' : ''
                    }`}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-text-primary">
                      {node.name}
                    </span>
                    <span className="block text-xs capitalize text-text-secondary">
                      {node.status}
                    </span>
                  </span>
                </button>
              ) : (
                <>
                  <p className="truncate text-sm font-medium text-text-primary">{node.name}</p>
                  <p className="text-xs capitalize text-text-secondary">{node.status}</p>
                </>
              )}
              {expandable && isOpen && <NodeDetail node={node} />}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
